/**
 * @fileoverview Shared authoring footer for the import-template editor wizard.
 * @module import-editor/editor-footer
 *
 * Responsibilities:
 * - Render Previous, Save Draft, Next, and final publish actions for the editor
 * - Surface the host-provided final CTA label for Gate 3 publish-and-import flows
 * - Keep footer rendering stable for non-structural editor state changes
 *
 * Dependencies:
 * - `lucide-react` for footer action icons
 *
 * Architecture:
 * - Presentation-only component fed by the shared wizard state machine
 * - No direct storage or API access
 *
 * Notes:
 * - The final action publishes the template; it does not create a second unnamed state
 */

import { memo } from 'react';
import { ChevronLeft, ChevronRight, Save, CheckSquare } from 'lucide-react';

export interface EditorFooterProps {
  currentTabIndex: number;
  totalTabs: number;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  onSaveTemplate: () => void;
  saving?: boolean;
  isLastTab: boolean;
  /** Overrides the publish button label on the final tab. Used
      by the Card 3 save-and-import flow. */
  saveTemplateLabel?: string;
}

// Memoized: see EditorTabBar for the rationale. None of the footer's props
// depend on transient editor-state fields (like fieldMappings), so stable
// props mean zero re-render on AI accept/dismiss clicks.
function EditorFooter({
  currentTabIndex,
  totalTabs,
  onBack,
  onNext,
  onSaveDraft,
  onSaveTemplate,
  saving = false,
  isLastTab,
  saveTemplateLabel,
}: EditorFooterProps) {
  return (
    <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-3 flex items-center justify-between gap-3">
      {/* Left — Back */}
      <button
        type="button"
        onClick={onBack}
        disabled={currentTabIndex === 0}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Previous
      </button>

      {/* Right — Next / Save Draft / Publish */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSaveDraft}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="h-3.5 w-3.5" />
          Save Draft
        </button>

        {isLastTab ? (
          <button
            type="button"
            onClick={onSaveTemplate}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <CheckSquare className="h-3.5 w-3.5" />
            {saving ? 'Saving…' : (saveTemplateLabel ?? 'Publish Template')}
          </button>
        ) : (
          <button
            type="button"
            onClick={onNext}
            disabled={currentTabIndex >= totalTabs - 1}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default memo(EditorFooter);
